import type { SpecConstraintViewModel } from 'api/types/product-type/SpecConstraintViewModel';
import { SpecSelectionMode, SpecValueType } from 'api/types/product-type/SpecConstraintViewModel';

/**
 * API returns SpecValueType as string enum names (JsonStringEnumConverter).
 * Normalize wire values to numeric SpecValueType for UI and query building.
 */
export function normalizeSpecValueType(valueType: unknown): SpecValueType {
  if (valueType === SpecValueType.Number || valueType === 0 || valueType === '0' || valueType === 'Number') {
    return SpecValueType.Number;
  }
  if (valueType === SpecValueType.String || valueType === 1 || valueType === '1' || valueType === 'String') {
    return SpecValueType.String;
  }
  if (valueType === SpecValueType.Enum || valueType === 2 || valueType === '2' || valueType === 'Enum') {
    return SpecValueType.Enum;
  }
  return SpecValueType.String;
}

/** Unwrap ServiceResult data: plain array or legacy paginated envelope. */
export function normalizeSpecConstraintList(data: unknown): SpecConstraintViewModel[] {
  if (data == null) return [];
  if (Array.isArray(data)) {
    return data.map(normalizeSpecConstraint);
  }
  if (typeof data === 'object' && 'items' in data) {
    const items = (data as { items?: unknown }).items;
    if (Array.isArray(items)) {
      return items.map(normalizeSpecConstraint);
    }
  }
  return [];
}

export function normalizeSpecConstraint(raw: SpecConstraintViewModel): SpecConstraintViewModel {
  const allowedValues = dedupeAllowedValues(raw.allowedValues ?? []);
  return {
    ...raw,
    valueType: normalizeSpecValueType(raw.valueType),
    selectionMode: normalizeSpecSelectionMode(raw.selectionMode),
    allowedValues,
  };
}

function dedupeAllowedValues(
  values: SpecConstraintViewModel['allowedValues'] | null | undefined
): SpecConstraintViewModel['allowedValues'] {
  if (!values) return [];
  const seen = new Set<string>();
  const result: NonNullable<SpecConstraintViewModel['allowedValues']> = [];
  for (const av of values) {
    const key = av.value.trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    result.push(av);
  }
  result.sort((a, b) => a.value.localeCompare(b.value, 'tr', { sensitivity: 'base' }));
  return result;
}

export function normalizeSpecSelectionMode(mode: unknown): SpecSelectionMode {
  if (mode === SpecSelectionMode.Multi || mode === 1 || mode === '1' || mode === 'Multi') {
    return SpecSelectionMode.Multi;
  }
  return SpecSelectionMode.Single;
}
